import { cn } from "@/lib/utils";

/**
 * Single-DOM responsive table.
 *
 * - Desktop (>=768px): standard HTML table.
 * - Mobile  (<768px):  rows collapse into stacked cards; each cell becomes a
 *   "label | value" flex row. Cell label comes from the column definition
 *   and is injected via CSS `::before { content: attr(data-label) }`,
 *   so there is exactly one set of markup — no duplicate DOM nodes.
 *
 * column: {
 *   key:          string  // React key & default value accessor
 *   label:        string  // header text + mobile label
 *   render?:      (row) => ReactNode
 *   align?:       'left' | 'right'   // desktop alignment
 *   mobileVariant?: 'title' | 'actions' | 'no-label'
 *                 // title    -> full-width, prominent (no label)
 *                 // actions  -> full-width right-aligned, top-separated
 *                 // no-label -> full-width, no label injected
 *   headerClassName?: string
 *   cellClassName?:   string
 * }
 */
export function ResponsiveTable({
  columns,
  rows,
  getRowKey = (row) => row.id,
  getRowTestId,
  testId,
  emptyMessage,
}) {
  if (rows.length === 0 && emptyMessage) {
    return emptyMessage;
  }

  return (
    <div className="rt-wrap" data-testid={testId}>
      <table className="rt">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(col.align === "right" && "rt-cell-right", col.headerClassName)}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={getRowKey(row)} data-testid={getRowTestId ? getRowTestId(row) : undefined}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  data-label={col.label}
                  className={cn(
                    col.align === "right" && "rt-cell-right",
                    col.mobileVariant === "title" && "rt-title",
                    col.mobileVariant === "actions" && "rt-actions",
                    col.mobileVariant === "no-label" && "rt-no-label",
                    col.cellClassName,
                  )}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
